import api from "../api/ApiService";

export const scriptRegex = /<[^>]*>/;
// Format: YYYY-MM-DD or YYYY-MM-DD HH:mm:ss, etc.
export const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (isNaN(date)) return ""; // Handle invalid dates

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  if (includeTime) {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  return `${year}-${month}-${day}`;
};

export const formatDateTime = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date)) return ""; // Handle invalid dates

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 → 12

  return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
};

export const camelCaseToLabel = (str) => {
  if (!str) return "";

  return str
    .replace(/([A-Z])/g, " $1") // Insert space before capital letters
    .replace(/^./, (char) => char.toUpperCase()); // Capitalize first letter
};

/**
 * Validates a single field
 */
export const validateField = (validation, value, label) => {
  if (!validation) return null;

  // Helper to get label text safely
  const getLabelText = (lang) => {
    if (label && typeof label === "object") return label[lang] || "";
    return typeof label === "string" ? label : "";
  };

  // ---------------- REQUIRED ----------------
  if (validation.required) {
    if (typeof value === "object" && value !== null) {
      // For document/file fields → check url
      if (!value.url) {
        return { hi: `${getLabelText("hi")} आवश्यक है`, en: `${getLabelText("en")} is required` };
      }
    } else if (!value) {
      // For text/number fields
      return { hi: `${getLabelText("hi")} आवश्यक है`, en: `${getLabelText("en")} is required` };
    }
  }

  // ---------------- PATTERN ----------------
  if (validation.pattern && value && typeof value === "string") {
    if (!validation.pattern.test(value)) {
      return {
        hi: validation.message?.hi || "Invalid input",
        en: validation.message?.en || "Invalid input",
      };
    }
  }

  return null;
};

/**
 * Validates entire form data based on field config
 * @param {Array} formSections - sections with fields (e.g. manualDataFieldSections)
 * @param {Object} formData - user filled values
 * @returns {Object} { errors, isValid }
 */
export const validateMultiSectionFormData = (formSections, formData) => {
  const errors = {};

  formSections.forEach((section) => {
    section.data?.forEach((subSection) => {
      subSection.fields?.forEach((field) => {
        const name = field.inputProps.name;
        const label = field.label || camelCaseToLabel(name);
        const validation = field.inputProps.validation || {};
        const value = formData[name];

        const error = validateField(validation, value, label);
        if (error) {
          errors[name] = error;
        }
      });
    });
  });

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};

export const validateSingleSectionFormData = (formSection, formData) => {
  const errors = {};

  formSection.forEach((section) => {
    section.fields?.forEach((field) => {
      const name = field.inputProps.name;
      const label = field.label || camelCaseToLabel(name);
      const validation = field.inputProps.validation || {};
      const value = formData[name];

      const error = validateField(validation, value, label);
      if (error) {
        errors[name] = error;
      }
    });
  });

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};

export const formatKeyNames = (category) => {
  return category
    ?.split("_")
    .map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1) // ✅ no .toLowerCase()
    )
    .join(" ");
};

export const mapFieldsToValues = (fields, apiData, language = false) => {
  return fields.reduce((acc, f) => {
    const fieldName = f.name || f.inputProps?.name;

    // 🔹 Only check f.label
    let label = "";

    if (typeof f.label === "object") {
      label = language ? f.label?.hi : f.label?.en;
    } else {
      label = f.label;
    }

    acc[label] = apiData?.[fieldName] || "--";
    return acc;
  }, {});
};

export const getLocalDate = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
};

export const getKeyByValue = (mapObj, value) => {
  if (!value) return null;

  const normalizedValue = value.trim().toLowerCase();

  return (
    Object.keys(mapObj).find(
      (key) => mapObj[key]?.trim().toLowerCase() === normalizedValue
    ) || null
  );
};

export const uploadDocumentToServer = async (uploadedFile) => {
  try {
    if (!uploadedFile) return null;

    const fileFormData = new FormData();
    fileFormData.append("upload_doc", uploadedFile);

    const uploadResponse = await api.post(
      "/application/document/upload/link",
      fileFormData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    if (uploadResponse?.success && uploadResponse?.data?.path) {
      return uploadResponse?.data?.path;
    }
  } catch (err) {
    return null;
  }
  return null;
};

export const numberToWords = (num) => {
  if (!num || isNaN(num)) return { en: "", hi: "" };

  const enA = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const enB = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const hiA = [
    "",
    "एक",
    "दो",
    "तीन",
    "चार",
    "पांच",
    "छह",
    "सात",
    "आठ",
    "नौ",
    "दस",
    "ग्यारह",
    "बारह",
    "तेरह",
    "चौदह",
    "पंद्रह",
    "सोलह",
    "सत्रह",
    "अठारह",
    "उन्नीस",
  ];

  const hiB = [
    "",
    "",
    "बीस",
    "तीस",
    "चालीस",
    "पचास",
    "साठ",
    "सत्तर",
    "अस्सी",
    "नब्बे",
  ];

  const inWords = (n, lang) => {
    const a = lang === "en" ? enA : hiA;
    const b = lang === "en" ? enB : hiB;

    if (n === 0) return "";
    if (n < 20) return a[n];
    if (n < 100)
      return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return (
        a[Math.floor(n / 100)] +
        (lang === "en" ? " Hundred" : " सौ") +
        (n % 100 ? " " + inWords(n % 100, lang) : "")
      );
    if (n < 100000)
      return (
        inWords(Math.floor(n / 1000), lang) +
        (lang === "en" ? " Thousand" : " हजार") +
        (n % 1000 ? " " + inWords(n % 1000, lang) : "")
      );
    if (n < 10000000)
      return (
        inWords(Math.floor(n / 100000), lang) +
        (lang === "en" ? " Lakh" : " लाख") +
        (n % 100000 ? " " + inWords(n % 100000, lang) : "")
      );

    return (
      inWords(Math.floor(n / 10000000), lang) +
      (lang === "en" ? " Crore" : " करोड़") +
      (n % 10000000 ? " " + inWords(n % 10000000, lang) : "")
    );
  };

  const number = parseInt(num, 10);

  return {
    en: number > 0 ? "₹ " + inWords(number, "en").trim() : "",
    hi: number > 0 ? "₹ " + inWords(number, "hi").trim() : "",
  };
};

export const numberToWordsCrore = (num) => {
  if (!num || isNaN(num)) return { en: "", hi: "" };

  const enA = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];

  const enB = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const hiA = ["", "एक", "दो", "तीन", "चार", "पांच", "छह", "सात", "आठ", "नौ", "दस",
    "ग्यारह", "बारह", "तेरह", "चौदह", "पंद्रह", "सोलह", "सत्रह", "अठारह", "उन्नीस"];

  const hiB = ["", "", "बीस", "तीस", "चालीस", "पचास", "साठ", "सत्तर", "अस्सी", "नब्बे"];

  const inWords = (n, lang) => {
    const a = lang === "en" ? enA : hiA;
    const b = lang === "en" ? enB : hiB;

    if (n === 0) return "";
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return a[Math.floor(n / 100)] + (lang === "en" ? " Hundred" : " सौ") +
        (n % 100 ? " " + inWords(n % 100, lang) : "");
    if (n < 100000)
      return inWords(Math.floor(n / 1000), lang) +
        (lang === "en" ? " Thousand" : " हजार") +
        (n % 1000 ? " " + inWords(n % 1000, lang) : "");
    if (n < 10000000)
      return inWords(Math.floor(n / 100000), lang) +
        (lang === "en" ? " Lakh" : " लाख") +
        (n % 100000 ? " " + inWords(n % 100000, lang) : "");

    return inWords(Math.floor(n / 10000000), lang) +
      (lang === "en" ? " Crore" : " करोड़") +
      (n % 10000000 ? " " + inWords(n % 10000000, lang) : "");
  };

  const value = parseFloat(num);

  const crorePart = Math.floor(value);
  const decimalPart = parseFloat((value - crorePart).toFixed(3));

  // Convert decimal → lakh + thousand
  const totalLakhs = decimalPart * 100;
  const lakhPart = Math.floor(totalLakhs);
  const thousandPart = Math.round((totalLakhs - lakhPart) * 100);

  return {
    en:
      "₹ " +
      (crorePart ? inWords(crorePart, "en") + " Crore " : "") +
      (lakhPart ? inWords(lakhPart, "en") + " Lakh " : "") +
      (thousandPart ? inWords(thousandPart, "en") + " Thousand" : "")
        .trim(),

    hi:
      "₹ " +
      (crorePart ? inWords(crorePart, "hi") + " करोड़ " : "") +
      (lakhPart ? inWords(lakhPart, "hi") + " लाख " : "") +
      (thousandPart ? inWords(thousandPart, "hi") + " हजार" : "")
        .trim(),
  };
};

export const stringEquals = (str1, str2, ignoreCase = false) => {
  if (typeof str1 !== "string" || typeof str2 !== "string") {
    return false; // avoid errors if null/undefined
  }
  return ignoreCase
    ? str1.toLowerCase() === str2.toLowerCase()
    : str1 === str2;
}

export const isInvalidNumber = (val) =>
  val === "" || val === null || val === undefined || isNaN(Number(val));